import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { CreditCard, Plus, Save, Trash2, Edit2, X, Repeat, RefreshCw, UserPlus, TrendingUp, BellRing, DollarSign } from 'lucide-react';
import { safeConfirm } from '../../lib/confirm';

const DURATIONS = [4, 6, 8, 12];

export default function AdminMemberships() {
  const [plans, setPlans] = useState([]);
  const [patients, setPatients] = useState([]);
  const [subs, setSubs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editPlan, setEditPlan] = useState(null);
  const [assignFor, setAssignFor] = useState(null);

  const loadPlans = () => api.get('/memberships/plans').then(r => setPlans(r.data));
  const loadPatients = () => api.get('/patients').then(r => setPatients(r.data));
  const loadSummary = () => api.get('/memberships/summary').then(r => setSummary(r.data)).catch(() => {});
  const loadSubs = async () => {
    const ps = (await api.get('/patients')).data;
    const all = [];
    for (const p of ps.slice(0, 50)) {
      try {
        const r = await api.get(`/memberships/patient/${p.user_id}`);
        for (const s of r.data) all.push({ ...s, patient_name: p.full_name, patient_code: p.patient_code });
      } catch (_) { /* skip */ }
    }
    setSubs(all);
  };

  useEffect(() => { loadPlans(); loadPatients(); loadSubs(); loadSummary(); }, []);

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">PACKAGES & MEMBERSHIPS</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Memberships</h1>
        <p className="text-fg-muted mt-2">Visit packs and monthly memberships. Patients see their card on /patient/membership.</p>
      </div>

      {summary && (
        <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="membership-summary">
          <SummaryStat icon={CreditCard} label="Active visit packs"
                       value={summary.active_visit_packs} color="brand"
                       testid="stat-packs"/>
          <SummaryStat icon={BellRing} label="Renewals due this month"
                       value={summary.renewals_due_this_month} color="warn"
                       testid="stat-renewals"/>
          <SummaryStat icon={DollarSign} label="Revenue (this quarter)"
                       value={summary.revenue_quarter?.length
                         ? summary.revenue_quarter.map(r => `${r.total.toFixed(2)} ${r.currency}`).join(' · ')
                         : '0.00 PLN'}
                       color="success"
                       testid="stat-revenue"
                       hint={`${summary.revenue_quarter?.reduce((a,r)=>a+r.count, 0) || 0} subscriptions since ${summary.quarter_starts}`}/>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="admin-card mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold flex items-center gap-2"><CreditCard className="w-4 h-4 text-brand"/> Catalogue</h3>
              <button onClick={()=>setEditPlan({ kind: 'visit_pack', currency: 'PLN', active: true })} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="new-plan-btn">
                <Plus className="w-4 h-4"/> New plan
              </button>
            </div>
            <table>
              <thead><tr><th>Name</th><th>Kind</th><th>Visits / months</th><th>Price</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {plans.map(p => (
                  <tr key={p.plan_id} data-testid={`plan-row-${p.plan_id}`}>
                    <td className="font-medium">{p.name}</td>
                    <td><span className={p.kind === 'visit_pack' ? 'badge-neutral' : 'badge-success'}>{p.kind}</span></td>
                    <td className="text-xs">{p.kind === 'visit_pack' ? `${p.visits_total} visits` : `${p.duration_months} months`}</td>
                    <td>{Number(p.price).toFixed(2)} <span className="text-xs text-fg-muted">{p.currency}</span></td>
                    <td><span className={p.active ? 'badge-success' : 'badge-neutral'}>{p.active ? 'active' : 'inactive'}</span></td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={()=>setEditPlan(p)} className="text-brand" data-testid={`edit-plan-${p.plan_id}`}><Edit2 className="w-3.5 h-3.5"/></button>
                        <button onClick={async ()=>{
                          if (!await safeConfirm(`Delete plan ${p.name}?`)) return;
                          await api.delete(`/memberships/plans/${p.plan_id}`); loadPlans();
                        }} className="text-[#B83A3A]" data-testid={`del-plan-${p.plan_id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!plans.length && <tr><td colSpan="6" className="text-center text-fg-muted py-8">No plans yet.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="admin-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Active subscriptions ({subs.length})</h3>
              <button onClick={loadSubs} className="text-xs text-brand inline-flex items-center gap-1" data-testid="refresh-subs">
                <RefreshCw className="w-3.5 h-3.5"/> Refresh
              </button>
            </div>
            <table>
              <thead><tr><th>Patient</th><th>Plan</th><th>Progress</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {subs.map(s => (
                  <tr key={s.membership_id} data-testid={`sub-row-${s.membership_id}`}>
                    <td>
                      <div className="font-medium">{s.patient_name}</div>
                      <div className="text-[10px] text-fg-muted font-mono">{s.patient_code}</div>
                    </td>
                    <td className="text-xs">{s.plan_name}</td>
                    <td className="text-xs">
                      {s.kind === 'visit_pack'
                        ? `${s.visits_used}/${s.visits_total} visits`
                        : (s.days_left !== null ? `${s.days_left} days left` : '—')}
                    </td>
                    <td><span className={s.status === 'active' ? 'badge-success' : 'badge-neutral'}>{s.status}</span></td>
                    <td>
                      <div className="flex gap-2 text-xs">
                        {s.kind === 'visit_pack' && s.status === 'active' && (
                          <button onClick={async ()=>{ await api.post(`/memberships/${s.membership_id}/use`); loadSubs(); }}
                                  className="text-brand" data-testid={`use-${s.membership_id}`}>Use 1 visit</button>
                        )}
                        {s.kind === 'membership' && (
                          <button onClick={async ()=>{ await api.post(`/memberships/${s.membership_id}/renew`); loadSubs(); }}
                                  className="text-brand inline-flex items-center gap-1" data-testid={`renew-${s.membership_id}`}>
                            <Repeat className="w-3 h-3"/> Renew
                          </button>
                        )}
                        <button onClick={async ()=>{
                          if (!await safeConfirm('Delete this subscription?')) return;
                          await api.delete(`/memberships/${s.membership_id}`); loadSubs();
                        }} className="text-[#B83A3A]" data-testid={`del-sub-${s.membership_id}`}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!subs.length && <tr><td colSpan="5" className="text-center text-fg-muted py-8">No active subscriptions.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-card lg:sticky lg:top-4 self-start">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><UserPlus className="w-4 h-4 text-brand"/> Assign to patient</h3>
          <select className="input mb-2" value={assignFor?.user_id || ''}
                  onChange={e=>setAssignFor(patients.find(p => p.user_id === e.target.value) || null)}
                  data-testid="assign-patient">
            <option value="">— pick patient —</option>
            {patients.map(p => <option key={p.user_id} value={p.user_id}>{`${p.full_name} (${p.patient_code})`}</option>)}
          </select>
          {assignFor && (
            <div className="space-y-2 mt-3">
              <div className="text-xs text-fg-muted">Pick a plan:</div>
              {plans.filter(p => p.active).map(p => (
                <button key={p.plan_id} onClick={async ()=>{
                  await api.post('/memberships/assign', { patient_id: assignFor.user_id, plan_id: p.plan_id });
                  loadSubs(); setAssignFor(null);
                }} className="surface border border-soft rounded-lg p-3 w-full text-left hover:border-brand" data-testid={`assign-plan-${p.plan_id}`}>
                  <div className="font-heading font-medium text-sm">{p.name}</div>
                  <div className="text-[11px] text-fg-muted mt-0.5">
                    {p.kind === 'visit_pack' ? `${p.visits_total} visits` : `${p.duration_months} months`} · {Number(p.price).toFixed(2)} {p.currency}
                  </div>
                </button>
              ))}
              {!plans.filter(p => p.active).length && (
                <div className="text-xs text-fg-muted">No active plans — create one first.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {editPlan && <PlanModal plan={editPlan} onClose={()=>setEditPlan(null)} onSaved={()=>{ loadPlans(); setEditPlan(null); loadSummary(); }}/>}
    </div>
  );
}

function SummaryStat({ icon: Icon, label, value, hint, color, testid }) {
  const ring = { brand: 'ring-brand/30 text-brand', warn: 'ring-warn/30 text-warn', success: 'ring-[#2A5C4E]/30 text-[#2A5C4E]' }[color] || '';
  return (
    <div className="surface border border-soft rounded-xl p-5" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg bg-white shadow-sm ring-1 ${ring} flex items-center justify-center`}>
        <Icon className="w-4 h-4"/>
      </div>
      <div className="mt-3 text-xs uppercase tracking-[0.18em] text-fg-muted font-medium">{label}</div>
      <div className="font-heading font-bold text-2xl mt-1">{value}</div>
      {hint && <div className="text-[11px] text-fg-muted mt-1">{hint}</div>}
    </div>
  );
}

function PlanModal({ plan, onClose, onSaved }) {
  const isNew = !plan.plan_id;
  const [form, setForm] = useState({ name: '', kind: 'visit_pack', visits_total: 5, duration_months: 4, price: 0, currency: 'PLN', description: '', active: true, ...plan });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        price: parseFloat(form.price) || 0,
        visits_total: form.kind === 'visit_pack' ? (parseInt(form.visits_total) || 1) : null,
        duration_months: form.kind === 'membership' ? (parseInt(form.duration_months) || 4) : null,
      };
      if (isNew) await api.post('/memberships/plans', body);
      else await api.put(`/memberships/plans/${plan.plan_id}`, body);
      onSaved();
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="plan-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-2xl" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-soft">
          <h2 className="font-heading font-semibold">{isNew ? 'New plan' : `Edit · ${plan.name}`}</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-3">
          <label className="block md:col-span-2">
            <div className="text-xs text-fg-muted mb-1">Name *</div>
            <input className="input" value={form.name} onChange={e=>set('name', e.target.value)} data-testid="plan-name"/>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Type</div>
            <select className="input" value={form.kind} onChange={e=>set('kind', e.target.value)} data-testid="plan-kind">
              <option value="visit_pack">Visit pack</option>
              <option value="membership">Membership</option>
            </select>
          </label>
          {form.kind === 'visit_pack' ? (
            <label className="block">
              <div className="text-xs text-fg-muted mb-1">Total visits (1–5 pack max recommended)</div>
              <input className="input" type="number" min="1" max="50" value={form.visits_total||5} onChange={e=>set('visits_total', e.target.value)} data-testid="plan-visits"/>
            </label>
          ) : (
            <label className="block">
              <div className="text-xs text-fg-muted mb-1">Duration (months)</div>
              <select className="input" value={form.duration_months||4} onChange={e=>set('duration_months', parseInt(e.target.value))} data-testid="plan-months">
                {DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Price</div>
            <input className="input" type="number" step="0.01" value={form.price} onChange={e=>set('price', e.target.value)} data-testid="plan-price"/>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Currency</div>
            <select className="input" value={form.currency} onChange={e=>set('currency', e.target.value)}>
              <option>PLN</option><option>EUR</option><option>USD</option><option>CAD</option><option>GBP</option>
            </select>
          </label>
          <label className="block md:col-span-2">
            <div className="text-xs text-fg-muted mb-1">Description</div>
            <textarea className="input min-h-[60px]" value={form.description||''} onChange={e=>set('description', e.target.value)}/>
          </label>
          <label className="block md:col-span-2 text-sm flex items-center gap-2">
            <input type="checkbox" checked={!!form.active} onChange={e=>set('active', e.target.checked)}/>
            Active (visible at registration / assignment)
          </label>
        </div>
        <div className="px-5 pb-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={saving || !form.name} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="plan-save">
            <Save className="w-4 h-4"/> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
